<template>
    <v-dialog v-if="dialog" v-model="dialog" fullscreen persistent @keydown.esc="cancel" @click:outside="cancel">
        <v-card height="100%" class="text-body-2 crm">
            <v-toolbar dark dense flat color="#2B3848">
                <v-toolbar-title class="text-body-1 font-weight-bold white--text">CRM</v-toolbar-title>
                <v-spacer />
                <v-btn icon @click="cancel">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <div class="d-flex" style="height: calc(100% - 48px)">
                <v-card tile elevation="8" class="d-flex flex-column fill-height" style="width: 400px">
                    <div v-if="bookingId || prebookingId" class="d-flex flex-column pa-2 mr-2" style="min-height: 350px">
                        <div class="d-flex align-center">
                            <img v-if="agencyImageId" :src="`/uploads/${agencyImageId}/64`" width="48" height="48" class="mt-2" />
                            <v-icon v-else size="48">mdi-head-question-outline</v-icon>

                            <div class="pl-4 flex-grow-1">
                                <span class="text-h6 font-weight-bold">{{ reference }}</span>
                                <span class="mt-1 d-flex">
                                    <span v-if="adults" class="d-flex align-center blue-grey darken-3 rounded white--text font-weight-medium" style="padding-left: 3px; padding-right: 3px">
                                        {{ adults }}
                                        <v-icon x-small color="blue-grey lighten-4">mdi-square-small</v-icon>
                                        <v-icon small color="blue-grey lighten-4" class="ml-0">mdi-account-multiple</v-icon>
                                    </span>
                                    <span v-if="children" class="d-flex ml-1 align-center blue-grey darken-3 rounded white--text font-weight-medium" style="padding-left: 3px; padding-right: 3px">
                                        {{ children }}
                                        <v-icon x-small color="blue-grey lighten-4">mdi-square-small</v-icon>
                                        <v-icon small color="blue-grey lighten-4" class="ml-0">mdi-human-female-boy</v-icon>
                                    </span>
                                    <span v-if="babies" class="d-flex ml-1 align-center blue-grey darken-3 rounded white--text font-weight-medium" style="padding-left: 3px; padding-right: 3px">
                                        {{ babies }}
                                        <v-icon x-small color="blue-grey lighten-4">mdi-square-small</v-icon>
                                        <v-icon small color="blue-grey lighten-4" class="ml-0">mdi-baby-carriage</v-icon>
                                    </span>

                                    <v-spacer />

                                    <span class="d-flex ml-1 align-center blue darken-3 rounded white--text font-weight-medium" style="padding-left: 6px; padding-right: 6px">INBOX</span>
                                </span>
                            </div>
                        </div>

                        <span class="d-flex align-center pl-3 pt-6">
                            <v-icon class="mr-2">mdi-calendar</v-icon>
                            <strong>{{ date }}</strong>
                        </span>

                        <div v-if="productId" class="d-flex align-start pl-3 pt-2">
                            <v-icon class="mr-2">mdi-ticket</v-icon>
                            <div class="d-flex flex-column" style="padding-top: 3px">
                                <span>
                                    {{ activity }}
                                </span>
                            </div>
                        </div>

                        <span class="d-flex align-center pl-3 pt-2">
                            <v-icon class="mr-2">mdi-bed</v-icon>
                            <span>{{ hotel }}</span>
                        </span>

                        <span class="d-flex align-center pl-3 pt-2">
                            <v-icon class="mr-2">mdi-bus-clock</v-icon>
                            <div class="d-flex flex-column" style="padding-top: 3px">
                                {{ pickupTime }} -
                                {{ meetingPoint }}
                            </div>
                        </span>

                        <span class="d-flex align-center pl-3 pt-2">
                            <v-icon class="mr-2">mdi-card-account-details-outline</v-icon>
                            <span>{{ name || "Nombre no especificado" }}</span>
                            <img class="ml-2" :src="`/images/flags/${language}.png`" height="20" />
                        </span>
                        <span class="d-flex mt-1 align-center pl-3 pt-2">
                            <v-icon class="mr-2">mdi-phone</v-icon>
                            {{ customerPhone || "Teléfono no especificado" }}
                        </span>
                        <span class="d-flex mt-1 align-center text-truncate pl-3 pt-2">
                            <v-icon class="mr-2">mdi-email</v-icon>
                            <span class="text-truncate">{{ customerEmail || "Email no especificado" }}</span>
                        </span>
                        <span class="d-flex mt-1 align-center pl-3 pt-2" v-if="comments">
                            <v-icon class="mr-2">mdi-comment-account-outline</v-icon>
                            <span>{{ comments }}</span>
                        </span>
                    </div>

                    <CrmTemplateList
                        v-if="bookingId || prebookingId"
                        class="flex-grow-1"
                        style="height: calc(100% - 350px)"
                        :type="messageTemplateType"
                        :autoSelectFirst="autoSelectFirst"
                        @select="selectTemplate"
                    />
                    <CrmTemplateList v-else class="flex-grow-1" style="height: calc(100%)" :type="messageTemplateType" :autoSelectFirst="autoSelectFirst" @select="selectTemplate" />
                </v-card>

                <div class="flex-grow-1 d-flex flex-column pa-4" style="max-width: calc(100% - 400px)">
                    <div class="d-flex align-center py-2" style="height: 64px">
                        <v-btn v-if="agencyId" small depressed :color="target === 'customer' ? 'primary' : 'white'" class="ml-4" @click="setCustomerTarget">
                            <v-icon v-if="target === 'customer'" class="mr-2">mdi-checkbox-marked-circle-outline</v-icon>
                            <v-icon v-else class="mr-2">mdi-checkbox-blank-circle-outline</v-icon>
                            Enviar al cliente
                        </v-btn>
                        <v-btn v-if="agencyId" small depressed :color="target === 'agency' ? 'primary' : 'white'" class="ml-4 mr-4" @click="setAgencyTarget">
                            <v-icon v-if="target === 'agency'" class="mr-2">mdi-checkbox-marked-circle-outline</v-icon>
                            <v-icon v-else class="mr-2">mdi-checkbox-blank-circle-outline</v-icon>
                            Enviar a la agencia
                        </v-btn>

                        <v-select
                            v-if="!agencyId"
                            outlined
                            hide-details
                            :items="languageNames"
                            item-text="name"
                            item-value="code"
                            label="Idioma"
                            v-model="language"
                            dense
                            class="mr-4"
                            style="max-width: 158px"
                        >
                            <template v-slot:selection="data">
                                <img v-if="data.item.code" :src="`/images/flags/${data.item.code}.png`" class="mr-2" style="width: 24px; height: 24px; object-fit: cover" />
                                <v-icon v-else style="margin-right: 8px">mdi-flag</v-icon>
                                <span>{{ data.item.name }}</span>
                            </template>
                            <template v-slot:item="data">
                                <div class="d-flex align-center" style="width: 100%">
                                    <img v-if="data.item.code" :src="`/images/flags/${data.item.code}.png`" class="mr-2" style="width: 24px; height: 24px; object-fit: cover" />
                                    <v-icon v-else style="margin-right: 8px">mdi-flag</v-icon>
                                    <span>{{ data.item.name }}</span>
                                </div>
                            </template>
                        </v-select>

                        <v-simple-checkbox v-if="bookingId || prebookingId || transferBookingId" v-model="sendEmail" dense hide-details color="primary" />
                        <v-text-field v-model="email" dense hide-details outlined label="Correo electrónico" placeholder="Correo electrónico" :disabled="!sendEmail" autocomplete="new" />
                        <v-simple-checkbox v-if="(bookingId || prebookingId || transferBookingId) && isSuperUser" v-model="sendWhatsApp" dense hide-details color="primary" class="ml-4" />
                        <span v-else style="width: 16px"></span>
                        <v-text-field v-if="isSuperUser" v-model="phone" dense hide-details outlined label="Teléfono" placeholder="Teléfono" :disabled="!sendWhatsApp" autocomplete="new" />
                    </div>

                    <div v-if="!agencyId" class="d-flex align-center py-2" style="height: 64px">
                        <v-text-field v-model="subject" dense hide-details outlined label="Asunto" placeholder="Asunto" autocomplete="new" />
                    </div>

                    <v-textarea v-if="conversationId" v-model="text" dense hide-details outlined solo height="200" placeholder="Escriba su mensaje" class="blue my-2" style="max-height: 200px" />
                    <v-textarea
                        v-else
                        v-model="text"
                        dense
                        hide-details
                        outlined
                        solo
                        height="100%"
                        placeholder="Escriba su mensaje"
                        class="my-2 v-textarea-wrapper"
                        style="height: calc(100% - 64px - 64px - 48px)"
                    />

                    <div class="d-flex align-center" style="height: 48px">
                        <LanguageButtonBar :value="sourceLanguage" @change="sourceLanguage = $event" />
                        <v-btn icon @click="swapLanguages" class="mx-4">
                            <v-icon>mdi-swap-horizontal</v-icon>
                        </v-btn>
                        <LanguageButtonBar :value="targetLanguage" @change="targetLanguage = $event" />
                        <v-btn color="primary" @click="translate" :disabled="!text" :loading="translating" class="ml-4">Traducir</v-btn>
                        <v-spacer />
                        <v-btn color="primary" @click="sendMessage" :disabled="!valid || translating" :loading="sending">Enviar</v-btn>
                    </div>

                    <ConversationView
                        v-if="conversationId"
                        :conversationId="conversationId"
                        :refreshToken="conversationRefreshToken"
                        @resend="resend"
                        style="height: calc(100% - 64px - 200px - 48px)"
                    />
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<style lang="scss">
.crm {
    .v-textarea-wrapper {
        background-color: red;

        .v-input__control {
            background-color: blue;
            height: 100%;
        }
    }
}
</style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { OpenCrmRequest, DataClient } from "@/services/DataClient";
import { Component, Vue, Watch } from "vue-property-decorator";
import { VBtn, VCard, VDialog } from "vuetify/lib";
import CrmTemplateList from "@/views/Crm/CrmTemplateList.vue";
import { BaseView } from "@/components/BaseView";
import ConversationView from "@/views/Crm/ConversationView.vue";

@Component({
    components: {
        CrmTemplateList,
        ConversationView,
    },
})
export default class extends BaseView {
    dialog = false;

    sourceLanguage = "es";
    targetLanguage = "en";
    translating = false;

    languageNames = [
        { code: "es", name: "Español" },
        { code: "en", name: "Inglés" },
        { code: "fr", name: "Francés" },
        { code: "de", name: "Alemán" },
        { code: "it", name: "Italiano" },
        { code: "cz", name: "Checo" },
        { code: "pt", name: "Portugués" },
        { code: "ru", name: "Ruso" },
        { code: "pl", name: "Polaco" },
    ];

    messageTemplateId = "";
    messageTemplateType = "";
    autoSelectFirst = false;

    target = "customer";
    sendEmail = false;
    sendWhatsApp = false;

    bookingId = "";
    prebookingId = "";
    transferBookingId = "";
    conversationId = "";

    productId = "";
    meetingPointId = "";
    hotelId = "";

    agencyId = "";
    agency = "";
    agencyImageId = "";
    reference = "";
    date = "";
    activity = "";
    meetingPoint = "";
    pickupTime = "";
    language = "";
    name = "";
    hotel = "";
    email = "";
    phone = "";
    comments = "";
    adults = 0;
    children = 0;
    babies = 0;

    customerEmail = "";
    customerPhone = "";
    subject = "";

    text = "";

    conversationRefreshToken = 0;
    sending = false;

    mounted() {
        EventBus.$on(DataClient.Crm.OpenCrmDialogEvent, this.handleOpenEvent);
    }

    beforeDestroy() {
        EventBus.$off(DataClient.Crm.OpenCrmDialogEvent, this.handleOpenEvent);
    }

    async handleOpenEvent(event: OpenCrmRequest) {
        this.messageTemplateId = "";
        this.messageTemplateType = event.messageTemplateType || "";
        this.autoSelectFirst = event.messageTemplateType ? true : false;

        this.productId = event.productId || "";
        this.meetingPointId = event.meetingPointId || "";
        this.hotelId = event.hotelId || "";
        this.pickupTime = event.pickupTime || "";

        if (event.productId) {
            this.bookingId = "";
            this.prebookingId = "";
            this.transferBookingId = "";
            this.conversationId = "";
            this.agencyId = "";
            this.agency = "";
            this.reference = "";
            this.date = "";
            this.activity = "";
            this.meetingPoint = "";
            this.language = "es";
            this.name = "";
            this.hotel = "";
            this.customerEmail = "";
            this.customerPhone = "";
            this.comments = "";

            this.target = "customer";
            this.sendEmail = true;
            this.sendWhatsApp = true;
        } else if (event.bookingId) {
            const booking = await DataClient.Bookings.get({ id: event.bookingId });

            this.bookingId = event.bookingId;
            this.prebookingId = "";
            this.transferBookingId = "";
            this.conversationId = booking.conversationId;

            this.agencyId = booking.agencyId;
            this.agency = booking.agencyName;
            this.reference = booking.reference;
            this.date = booking.date;
            this.activity = booking.productName;
            this.meetingPoint = booking.meetingPointName;
            this.pickupTime = booking.pickupTime;
            this.language = booking.language;
            this.name = booking.customerName;
            this.hotel = booking.hotelName;
            this.customerEmail = booking.customerEmail;
            this.customerPhone = booking.customerPhone;
            this.comments = booking.comments;
            this.target = "customer";
            this.sendEmail = false;
            this.sendWhatsApp = false;
        } else if (event.transferBookingId) {
            const booking = await DataClient.TransferBookings.get({ id: event.transferBookingId });

            this.bookingId = "";
            this.prebookingId = "";
            this.transferBookingId = event.transferBookingId;
            this.conversationId = "";

            this.agencyId = booking.agencyId;
            this.agency = booking.agencyName;
            this.reference = booking.reference;
            this.date = booking.date;
            this.activity = "";
            this.meetingPoint = "";
            this.pickupTime = "";
            this.language = "de";
            this.name = booking.customerFullName;
            this.hotel = "";
            this.customerEmail = booking.customerEmail;
            this.customerPhone = booking.customerPhone;
            this.comments = booking.comments;
            this.target = "customer";
            this.sendEmail = false;
            this.sendWhatsApp = false;
        } else if (event.prebookingId) {
            const booking = await DataClient.Prebookings.get({ id: event.prebookingId });

            this.bookingId = "";
            this.prebookingId = event.prebookingId;
            this.conversationId = booking.conversationId;

            this.agencyId = booking.agencyId;
            this.agency = booking.agencyName;
            this.reference = booking.reference;
            this.date = booking.date;
            this.activity = booking.productName;
            this.meetingPoint = booking.meetingPointName;
            this.pickupTime = booking.pickupTime;
            this.language = booking.language;
            this.name = booking.customerName;
            this.hotel = booking.hotelName;
            this.customerEmail = booking.customerEmail;
            this.customerPhone = booking.customerPhone;
            this.comments = booking.comments;

            this.adults = booking.adults;
            this.children = booking.children;
            this.babies = booking.babies;

            this.target = "customer";
            this.sendEmail = false;
            this.sendWhatsApp = false;
        }

        this.email = this.customerEmail;
        this.phone = this.customerPhone;

        if (this.agencyId && !event.transferBookingId) {
            const agency = await DataClient.Agencies.get({ id: this.agencyId });

            this.agencyImageId = agency?.imageId;
        }

        this.subject = "";
        this.text = "";

        if (this.language !== "es") {
            this.targetLanguage = this.language;
        }

        this.dialog = true;
    }

    accept() {
        this.dialog = false;

        DataClient.Crm.open(true, true);
    }

    cancel() {
        this.dialog = false;

        DataClient.Crm.open(false, true);
    }

    setCustomerTarget() {
        this.target = "customer";

        this.email = this.customerEmail;
        this.phone = this.customerPhone;

        this.sendEmail = false;
        this.sendWhatsApp = false;
    }

    async setAgencyTarget() {
        if (this.transferBookingId) {
            this.target = "agency";

            this.email = "info@alltours.de";
            this.phone = "+34651610173";

            this.sendEmail = false;
            this.sendWhatsApp = false;
        } else {
            const agency = await DataClient.Agencies.get({ id: this.agencyId });

            this.email = agency.operationalInformation.email;
            this.phone = agency.operationalInformation.phone;

            this.target = "agency";

            this.sendEmail = false;
            this.sendWhatsApp = false;
        }
    }

    @Watch("language")
    async onLanguageChanged() {
        if (this.messageTemplateId) {
            await this.renderTemplate();
        }
    }

    async selectTemplate(id: string) {
        this.messageTemplateId = id;
        await this.renderTemplate();
    }

    async renderTemplate() {
        const template = await DataClient.MessageTemplates.render({
            id: this.messageTemplateId,
            language: this.target === "agency" ? "es" : this.language,
            productId: this.productId,
            meetingPointId: this.meetingPointId,
            hotelId: this.hotelId,
            pickupTime: this.pickupTime,
            bookingId: this.bookingId,
            prebookingId: this.prebookingId,
            transferBookingId: this.transferBookingId,
        });

        this.text = template.content;
    }

    async sendMessage() {
        this.sending = true;

        await this.secureExecution(async () => {
            const r = await DataClient.Conversations.sendMessage({
                bookingId: this.bookingId,
                prebookingId: this.prebookingId,
                subject: this.subject || "",
                text: this.text,
                email: this.sendEmail ? this.email : "",
                whatsAppPhoneNumber: this.sendWhatsApp ? this.phone : "",
            });

            this.text = "";

            this.conversationId = r.id;
            this.conversationRefreshToken++;
        });

        this.sending = false;

        if (!this.agencyId) {
            this.cancel();
        }
    }

    get valid() {
        if (!this.email && !this.phone) {
            return false;
        }

        if (!this.sendEmail && !this.sendWhatsApp) {
            return false;
        }

        if (this.sendEmail && !this.email) {
            return false;
        }

        if (this.sendEmail && this.email && !this.emailIsValid(this.email)) {
            return false;
        }

        if (this.sendEmail && this.email && !this.agency && !this.subject) {
            return false;
        }

        if (this.sendWhatsApp && !this.phone) {
            return false;
        }

        if (!this.text) {
            return false;
        }

        return true;
    }

    emailIsValid(email: string) {
        const replaced = (email || "").replaceAll(";", ",");
        const emails = replaced.split(",");

        for (const singleEmail of emails) {
            const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(singleEmail);
            if (!valid) {
                return false;
            }
        }

        return true;
    }

    resend(text: string) {
        this.text = text;
    }

    swapLanguages() {
        const source = this.sourceLanguage;
        this.sourceLanguage = this.targetLanguage;
        this.targetLanguage = source;
    }

    async translate() {
        this.translating = true;
        const r = await DataClient.TextTranslation.translate({
            text: this.text,
            sourceLanguage: this.sourceLanguage,
            targetLanguage: this.targetLanguage,
        });
        this.text = r.text;
        this.translating = false;
    }
}
</script>
